import {
  ArrowDownToLine, Boxes, History, CalendarDays, PackageCheck, Truck, Bike, UserCheck,
  Receipt, Users, MessageSquare, Search, Tags, Percent, Megaphone, Settings,
  ScanSearch, ImagePlus, ListChecks, BookOpen,
} from 'lucide-react';

// Single source for the sidebar, the breadcrumbs and the ⌘K "Go to" list.
// `role` gates who sees an item: 'all' | 'core' (core_team + admin) | 'admin'.
// `subTabs` are the page's own tabs — the palette can deep-link into them via
// navigate(page, { subTab }). Their keys must match the page's tab keys.
export const NAV_GROUPS = [
  {
    label: 'Warehouse',
    items: [
      { key: 'inbound', path: 'inbound', label: 'Inbounding', icon: ArrowDownToLine, role: 'all', subTabs: [
        { key: 'preentry', label: 'Pre-Entry' },
        { key: 'receiving', label: 'Receiving' },
        { key: 'putaway', label: 'Putaway' },
        { key: 'rejected', label: 'Rejected' },
        { key: 'warehouse', label: 'Warehouse' },
      ] },
      { key: 'inventory', path: 'inventory',   label: 'Inventory',    icon: Boxes,   role: 'all' },
      { key: 'itemhistory', path: 'item-history', label: 'Item History', icon: History, role: 'all' },
    ],
  },
  {
    label: 'Orders',
    items: [
      { key: 'appointments', path: 'appointments', label: 'Appointments', icon: CalendarDays, role: 'all' },
      { key: 'orderops', path: 'order-ops', label: 'Order Ops', icon: PackageCheck, role: 'all', subTabs: [
        { key: 'packing', label: 'Packing' },
        { key: 'predispatch', label: 'Pre-Dispatch' },
        { key: 'dispatch', label: 'Dispatch' },
        { key: 'reversal', label: 'Reversal' },
        { key: 'admin_reversal', label: 'Admin Reversal' },
        { key: 'restock', label: 'Restock' },
        { key: 'ironing', label: 'Ironing' },
        { key: 'retagging', label: 'Retagging' },
        { key: 'search', label: 'Search Queue', role: 'core' },
        { key: 'receipt', label: 'Generate Invoice', role: 'core' },
        { key: 'pricematch', label: 'Price Match', role: 'core' },
        { key: 'courier', label: 'Catalog — no rider', role: 'core' },
        { key: 'slots', label: 'Slots Manage', role: 'core' },
      ] },
      { key: 'intransit', path: 'in-transit',     label: 'In Transit',     icon: Truck,     role: 'all' },
      { key: 'riderassign', path: 'assign-rider',   label: 'Assign Rider',   icon: Bike,      role: 'core' },
      { key: 'riderapproval', path: 'rider-approval', label: 'Rider Approval', icon: UserCheck, role: 'admin' },
    ],
  },
  {
    label: 'Sales & Customers',
    items: [
      { key: 'pastorders', path: 'past-orders', label: 'Past Orders', icon: Receipt, role: 'admin' },
      { key: 'crm', path: 'crm',        label: 'CRM',         icon: Users,   role: 'admin' },
      { key: 'messaging', path: 'messaging',  label: 'Messaging',   icon: MessageSquare, role: 'admin', subTabs: [
        { key: 'carts', label: 'Abandoned Carts' },
        { key: 'wati',  label: 'WATI Templates' },
      ] },
      { key: 'lookup', path: 'lookup',     label: 'Lookup',      icon: Search,  role: 'admin' },
    ],
  },
  {
    label: 'Brands & Pricing',
    items: [
      { key: 'brandops', path: 'brand-ops', label: 'Brand Ops', icon: Tags, role: 'core', subTabs: [
        { key: 'sor', label: 'SOR' },
        { key: 'brandperf', label: 'Brand Performance' },
        { key: 'invhealth', label: 'Inventory Health' },
        { key: 'categorysales', label: 'Category Sales' },
        { key: 'rtorecords', label: 'RTO Records' },
        { key: 'markimages', label: 'Mark Images' },
        { key: 'brandreturns', label: 'Brand Returns' },
        { key: 'benchmarking', label: 'Benchmarking' },
        { key: 'riderreturns', label: 'Rider Returns' },
        { key: 'riderfreeshoes', label: 'Rider Free Shoes' },
        { key: 'audit', label: 'Audit' },
        { key: 'tempsor', label: 'TempSOR', role: 'admin' },
      ] },
      { key: 'sale', path: 'sale', label: 'Sale', icon: Percent, role: 'admin' },
    ],
  },
  {
    label: 'Growth & Settings',
    items: [
      { key: 'adtracker', path: 'ad-tracker',         label: 'Ad Tracker',          icon: Megaphone, role: 'admin' },
      { key: 'SkipzyAppSettings', path: 'app-settings', label: 'Skipzy App Settings', icon: Settings,  role: 'admin' },
    ],
  },
  {
    // The customer catalogue pipeline, in order: pull + scrape, then photos,
    // then attributes, then everything together. Images and Attributes are
    // placeholders until those steps are built.
    label: 'Catalogue',
    items: [
      { key: 'cataloguepull',       path: 'catalogue-pull',       label: 'Pull & scrape',  icon: ScanSearch, role: 'core' },
      { key: 'catalogueimages',     path: 'catalogue-images',     label: 'Images',         icon: ImagePlus,  role: 'core' },
      { key: 'catalogueattributes', path: 'catalogue-attributes', label: 'Attributes',     icon: ListChecks, role: 'core' },
      { key: 'cataloguedata',       path: 'catalogue',            label: 'Full catalogue', icon: BookOpen,   role: 'core' },
      // Hidden for now, see App.jsx (also re-add TextSearch to the import above).
      // { key: 'cataloguesearch',     path: 'catalogue-search',     label: 'Search',         icon: TextSearch,   role: 'core' },
    ],
  },
];

export function canSee(role, auth) {
  const level = auth?.access_level;
  if (!role || role === 'all') return true;
  if (role === 'admin') return level === 'admin';
  if (role === 'core') return level === 'admin' || level === 'core_team';
  return false;
}

// Groups with only the items (and sub-tabs) this user may see; empty groups dropped.
export function visibleGroups(auth) {
  return NAV_GROUPS
    .map(g => ({
      ...g,
      items: g.items
        .filter(i => canSee(i.role, auth))
        .map(i => ({ ...i, subTabs: (i.subTabs || []).filter(t => canSee(t.role, auth)) })),
    }))
    .filter(g => g.items.length > 0);
}

// ── URLs ───────────────────────────────────────────────────────────────
// Every page (and sub-tab) has its own URL so a reload or a shared link lands
// on the same screen: /order-ops/admin-reversal, /brand-ops/benchmarking, …
const slug = key => key.replace(/_/g, '-');

export function pathFor(page, subTab) {
  const item = findNavItem(page)?.item;
  if (!item) return '/';
  return subTab ? `/${item.path}/${slug(subTab)}` : `/${item.path}`;
}

// '/order-ops/admin-reversal' -> { page: 'orderops', subTab: 'admin_reversal' }; null if unknown.
export function parsePath(pathname) {
  const [first, second] = pathname.split('/').filter(Boolean);
  if (!first) return null;
  for (const g of NAV_GROUPS) {
    const item = g.items.find(i => i.path === first);
    if (!item) continue;
    const sub = second && (item.subTabs || []).find(t => slug(t.key) === second);
    return { page: item.key, subTab: sub ? sub.key : undefined };
  }
  return null;
}

export function findNavItem(key) {
  for (const g of NAV_GROUPS) {
    const item = g.items.find(i => i.key === key);
    if (item) return { group: g, item };
  }
  return null;
}
